import JSZip from 'jszip';
import { FieldMapping } from './storage';

// Lightweight format parsers

export interface ParsedCSV {
  headers: string[];
  rows: Record<string, any>[];
}

// CSV parser (simple, handles quoted fields)
export function parseCSV(text: string): ParsedCSV {
  if (!text) return { headers: [], rows: [] };
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };

  let headerIndex = 0;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(',') || lines[i].includes(';')) {
      headerIndex = i;
      break;
    }
  }

  const headers = splitCSVLine(lines[headerIndex]);
  const rows: Record<string, any>[] = [];

  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = lines[i];
    if (headers.length > 1 && !line.includes(',') && !line.includes(';')) continue;
    if (line === lines[headerIndex]) continue;

    const cols = splitCSVLine(line);
    if (cols.length === 0) continue;
    const row: Record<string, any> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = cols[j] || '';
    }
    rows.push(row);
  }
  return { headers, rows };
}

export function parseCSVBlocks(text: string): ParsedCSV[] {
  if (!text) return [{ headers: [], rows: [] }];

  const lines = text.split(/\r?\n/);
  const blocks: string[][] = [];
  let currentBlock: string[] = [];

  for (let i = 0; i < lines.length; i++) {
     const line = lines[i].trim();
     if (line.length === 0) {
        if (currentBlock.length > 0) {
            blocks.push(currentBlock);
            currentBlock = [];
        }
     } else {
        currentBlock.push(lines[i]);
     }
  }
  if (currentBlock.length > 0) blocks.push(currentBlock);

  const results: ParsedCSV[] = [];
  for (const blockLines of blocks) {
      const blockText = blockLines.join('\n');
      const parsed = parseCSV(blockText);
      if (parsed.rows.length > 0) {
          results.push(parsed);
      }
  }

  if (results.length === 0) return [{ headers: [], rows: [] }];
  return results;
}

export function splitCSVLine(line: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i+1] === '"') {
        cur += '"'; i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      result.push(cur); cur = ''; continue;
    }
    cur += ch;
  }
  result.push(cur);
  return result.map(s => s.trim());
}

// JSON parser: accepts either an array of objects or an object with a top-level list
export function parseJSON(text: string): { rows: any[] } {
  if (!text) return { rows: [] };
  try {
    const v = JSON.parse(text);
    if (Array.isArray(v)) return { rows: v };
    // try common shapes: { items: [...] } or { data: [...] }
    if (v && typeof v === 'object') {
      if (Array.isArray(v.items)) return { rows: v.items };
      if (Array.isArray(v.data)) return { rows: v.data };
      // if object is a map of objects, convert to list
      const keys = Object.keys(v);
      if (keys.length > 0 && keys.every(k => typeof v[k] === 'object')) {
        return { rows: keys.map(k => v[k]) };
      }
    }
  } catch (e) {
    // Ignore invalid JSON
  }
  return { rows: [] };
}

// Very small YAML subset parser to cover simple lists of mappings like:
// - name: Foo
//   year: 2020
// - name: Bar
export function parseYAML(text: string): { rows: any[] } {
  if (!text) return { rows: [] };
  const lines = text.split(/\r?\n/);
  const rows: any[] = [];
  let cur: any = null;
  for (let raw of lines) {
    const line = raw.replace(/\t/g, '  ').replace(/\r/g,'');
    if (!line.trim()) continue;
    if (/^\s*-\s*/.test(line)) {
      if (cur) rows.push(cur);
      cur = {};
      const rest = line.replace(/^\s*-\s*/, '');
      if (rest) {
        const m = rest.match(/^([^:]+):\s*(.*)$/);
        if (m) cur[m[1].trim()] = tryParseValue(m[2].trim());
      }
      continue;
    }
    const m = line.match(/^\s*([^:]+):\s*(.*)$/);
    if (m && cur) cur[m[1].trim()] = tryParseValue(m[2].trim());
  }
  if (cur) rows.push(cur);
  return { rows };
}

export function tryParseValue(s: string): any {
  if (s === '') return '';
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  if (/^\d+\.\d+$/.test(s)) return parseFloat(s);
  if (/^(true|false)$/i.test(s)) return s.toLowerCase() === 'true';
  // unquote
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  return s;
}

// Normalize an input text by extension hint and return rows array
export function parseByFilename(name: string, text: string): ParsedCSV[] {
  const ext = (name || '').toLowerCase();

  if (ext.endsWith('.csv')) {
    return parseCSVBlocks(text);
  }
  if (ext.endsWith('.json')) {
    const rows = parseJSON(text).rows;
    return [{ headers: rows.length > 0 ? Object.keys(rows[0]) : [], rows }];
  }
  if (ext.endsWith('.yml') || ext.endsWith('.yaml')) {
    const rows = parseYAML(text).rows;
    return [{ headers: rows.length > 0 ? Object.keys(rows[0]) : [], rows }];
  }

  // fallback: try JSON, then CSV, then YAML
  try {
    const j = JSON.parse(text);
    if (Array.isArray(j)) return [{ headers: j.length > 0 ? Object.keys(j[0]) : [], rows: j }];
  } catch (e) {}

  const csvBlocks = parseCSVBlocks(text);
  if (csvBlocks.length > 0 && csvBlocks.some(b => b.rows.length > 0)) return csvBlocks;

  const y = parseYAML(text);
  const yRows = y.rows || [];
  return [{ headers: yRows.length > 0 ? Object.keys(yRows[0]) : [], rows: yRows }];
}

export async function parseZipContent(file: File | Blob): Promise<{ fileName: string, headers: string[], rows: Record<string, any>[] }[]> {
  const zip = new JSZip();
  const loadedZip = await zip.loadAsync(file);
  const results: { fileName: string, headers: string[], rows: Record<string, any>[] }[] = [];

  for (const [path, zipEntry] of Object.entries(loadedZip.files)) {
    if (!zipEntry.dir) {
      try {
        const text = await zipEntry.async('text');
        const blocks = parseByFilename(zipEntry.name, text);
        for (let i = 0; i < blocks.length; i++) {
          const { headers, rows } = blocks[i];
          if (rows && rows.length > 0) {
            const name = blocks.length > 1 ? `${path} (${i + 1})` : path;
            results.push({ fileName: name, headers, rows });
          }
        }
      } catch (e) {
        // ignore parsing errors and proceed thoroughly
      }
    }
  }
  return results;
}

export function autoSuggestMapping(
  headers: string[],
  rows: any[],
  totalRowsInAllFiles: Record<string, number>
): Partial<FieldMapping> {
  const newMap: Partial<FieldMapping> = {};
  if (!headers || !headers.length) return newMap;

  const hLower = headers.map(h => h.toLowerCase());

  const findMatch = (patterns: string[]) => {
      for (const p of patterns) {
          const idx = hLower.findIndex(h => h.includes(p));
          if (idx !== -1) return headers[idx];
      }
      return '';
  };

  const assignIfFound = (key: keyof FieldMapping, patterns: string[]) => {
      const match = findMatch(patterns);
      if (match) {
          (newMap as any)[key] = match;
      }
  };

  assignIfFound('title', ['title', 'name', 'film', 'movie', 'show', 'titre']);
  assignIfFound('year', ['year', 'annee', 'année', 'release']);
  assignIfFound('type', ['type', 'kind', 'media', 'genre']);
  assignIfFound('season', ['season', 'saison']);
  assignIfFound('episode', ['episode', 'épisode', 'ep']);
  assignIfFound('date', ['date', 'seen', 'watched', 'log', 'vu']);
    assignIfFound('scrapeUrlColumn', ['url', 'uri', 'link', 'lien']);

    // If an ID-like or URL-like column was found, we should default into ID mode or let the URL fetching trigger
    if (newMap.scrapeUrlColumn) {
        newMap.isIdMode = true;
    }

  // Calculate category based on file sizes
  const rowCount = rows ? rows.length : 0;
  if (rowCount > 0 && totalRowsInAllFiles) {
      const allCounts = Object.values(totalRowsInAllFiles);
      const maxRows = allCounts.length > 0 ? Math.max(...allCounts) : 0;

      // The file with the most rows could be categorized as 'seen' if we don't know elsewhere
      if (rowCount >= maxRows) {
          if (newMap.date) {
            newMap.category = 'seen';
          } else {
            newMap.category = 'seen'; // Still prefer seen if it is the largest file
          }
      } else {
          // If it's smaller, might be a watchlist or lists depending on name, but we leave it to manual if ambiguous
      }
  }

  return newMap;
}
